<template>
  <div class="LoginForm">
    <h1 class="font-family-display font-weight-bold mb-8">
      {{ $t('login') }}
    </h1>
    <p :class="[hasWrongCredentialsError ? 'mb-24' : 'mb-32']">
      {{ $t('dont_have_an_account_yet') }}
      <router-link :to="{ name: 'StageOnboarding' }">
        {{ $t('create_an_account') }}
      </router-link>
    </p>

    <div v-if="hasWrongCredentialsError" class="mb-32">
      <CardWrapper type-class="card--error" left-icon-class="icon-circle-close">
        <template #body>
          {{ $t('incorrect_credentials_message') }}
        </template>
      </CardWrapper>
    </div>

    <form
      class="flex flex-col items-center"
      novalidate="true"
      @submit.prevent="sendLoginData"
    >
      <InputField
        id="emailLogin"
        v-model="form.email"
        type="text"
        autocomplete="on"
        left-icon-class="icon-mail-envelope"
        right-icon-class="icon-close input__right-icon--hover"
        :label="$t('email')"
        :has-error="v$.email.$invalid && v$.email.$anyDirty"
        :has-right-reset-button="true"
        @on-right-click="form.email = ''"
      />

      <InputField
        id="passwordLogin"
        v-model="form.password"
        type="password"
        autocomplete="on"
        left-icon-class="icon-padlock"
        :label="$t('password')"
        :has-error="v$.password.$invalid && v$.password.$anyDirty"
      />

      <button
        class="btn btn--purple"
        :class="{ 'btn--loading': loading }"
        :disabled="v$.$invalid"
        type="submit"
      >
        <template v-if="loading">
          <LoadingDots />
        </template>
        <template v-else>
          {{ $t('login') }}
          <span class="icon-simple-arrow-right btn__icon"></span>
        </template>
      </button>
    </form>

    <div class="flex items-center space-between flex-col md-flex-row">
      <CheckboxField
        v-model:checked="form.keepMeLoggedIn"
        class="mb-24 md-mb-0"
        field-id="keepMeLoggedIn"
        :label="$t('keep_me_logged_in')"
      />
      <router-link :to="{ name: 'PasswordResetEmailView' }">
        {{ $t('forgot_password') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { LoadingDots, CardWrapper } from '@snowshoe-frontend/shared-components'
import {
  InputField,
  CheckboxField
} from '@snowshoe-frontend/form-fields-components'
import useVuelidate from '@vuelidate/core'
import { required, email } from '@vuelidate/validators'
import { useAuthStore } from '@/stores/authStore'
import { useRouter, useRoute } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

const form = reactive({
  email: '',
  password: '',
  keepMeLoggedIn: false
})
const loading = ref(false)
const hasWrongCredentialsError = ref(false)

const rules = {
  email: { required, email, $autoDirty: true },
  password: { required, $autoDirty: true }
}

const v$ = useVuelidate(rules, form)

async function sendLoginData() {
  try {
    loading.value = true
    hasWrongCredentialsError.value = false
    await authStore.login({ ...form })

    await router.push({ name: 'WelcomeView', query: { ...route.query } })
  } catch (e) {
    hasWrongCredentialsError.value = true
    form.password = ''
  } finally {
    loading.value = false
  }
}
</script>
